import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  CarFront,
  Check,
  CheckCircle2,
  ChevronRight,
  Download,
  ExternalLink,
  ImageIcon,
  Menu,
  Monitor,
  Plus,
  Radio,
  Search,
  Share2,
  Smartphone,
  Trophy,
  UsersRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { siteConfig } from "./siteConfig";
import { appRelease, roleHighlights } from "./appUpdates";
import featureCatalogSource from "./funktionsumfang.txt?raw";

type Device = "ios" | "android" | "desktop";
const screenshotDate = "10. September 2026";
type FeatureMotion = "calendar" | "response" | "squad" | "live" | "table" | "ride";

type CatalogItem = {
  text: string;
  details: string[];
};

type CatalogGroup = {
  title: string;
  items: CatalogItem[];
};

type Feature = {
  title: string;
  copy: string;
  icon: LucideIcon;
  number: string;
  previewFile: string;
  motion: FeatureMotion;
  motionLabel: string;
  motionValue: string;
  wide?: boolean;
};

const features: Feature[] = [
  {
    title: "Termine & Training",
    copy: "Training, Spiele und Veranstaltungen – für alle sofort im Blick.",
    icon: CalendarDays,
    number: "01",
    previewFile: "termine-2026-09.png",
    motion: "calendar",
    motionLabel: "VEREINSKALENDER",
    motionValue: "Alle Termine im Blick",
    wide: true,
  },
  {
    title: "Rückmeldungen",
    copy: "Zusagen, absagen oder Abwesenheiten vorab planen – für jedes Kind.",
    icon: CheckCircle2,
    number: "02",
    previewFile: "rueckmeldungen-2026-09.png",
    motion: "response",
    motionLabel: "RÜCKMELDUNG",
    motionValue: "Einfach zu- oder absagen",
  },
  {
    title: "Mannschaft & Kader",
    copy: "Kader planen und Gastspieler aus anderen Teams oder Jugenden nominieren.",
    icon: UsersRound,
    number: "03",
    previewFile: "kader-2026-09.png",
    motion: "squad",
    motionLabel: "SPIELTAGSKADER",
    motionValue: "Gemeinsam spielbereit",
  },
  {
    title: "Liveticker",
    copy: "Beim Spiel dabei sein – auch wenn du gerade nicht am Platz bist.",
    icon: Radio,
    number: "04",
    previewFile: "liveticker-2026-09.png",
    motion: "live",
    motionLabel: "LIVETICKER",
    motionValue: "Spielzeit & Spielstand",
    wide: true,
  },
  {
    title: "BfV & Ergebnisse",
    copy: "Die offizielle BfV-Ansicht öffnen – klar getrennt von den eigenen App-Spielen.",
    icon: Trophy,
    number: "05",
    previewFile: "bfv-2026-09.png",
    motion: "table",
    motionLabel: "OFFIZIELLE BFV-DATEN",
    motionValue: "Direkt zur Verbandsansicht",
  },
  {
    title: "Mitfahrten",
    copy: "Plätze anbieten und direkt für dich und deine Kinder buchen.",
    icon: CarFront,
    number: "06",
    previewFile: "mitfahrten-2026-09.png",
    motion: "ride",
    motionLabel: "FAHRGEMEINSCHAFT",
    motionValue: "Suchen oder anbieten",
  },
];

const showcaseFeatures = [features[3], features[2], features[5]];

function moveTab(event: React.KeyboardEvent<HTMLButtonElement>) {
  const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []);
  const index = tabs.indexOf(event.currentTarget);
  const next = event.key === "ArrowRight" ? (index + 1) % tabs.length
    : event.key === "ArrowLeft" ? (index - 1 + tabs.length) % tabs.length
      : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : -1;
  if (next < 0 || !tabs[next]) return;
  event.preventDefault();
  tabs[next].focus();
  tabs[next].click();
}

function TeamHighlights() {
  const [selectedRole, setSelectedRole] = useState(0);
  const role = roleHighlights[selectedRole];

  return (
    <div className="team-highlights">
      <div className="release-note"><CheckCircle2 size={18} aria-hidden="true" />
        <span>Funktionsstand {appRelease.version} · abgeglichen am {appRelease.checkedAt}</span>
      </div>
      <div className="role-tabs" role="tablist" aria-label="Vorteile nach Rolle">
        {roleHighlights.map((item, index) => (
          <button key={item.id} type="button" role="tab" id={`role-tab-${item.id}`}
            aria-selected={selectedRole === index} aria-controls="role-panel"
            tabIndex={selectedRole === index ? 0 : -1} onKeyDown={moveTab}
            onClick={() => setSelectedRole(index)}>{item.label}</button>
        ))}
      </div>
      <div id="role-panel" role="tabpanel" aria-labelledby={`role-tab-${role.id}`} tabIndex={0}>
        <div className="role-intro"><h3>{role.headline}</h3><p>{role.intro}</p></div>
        <div className="role-card-grid">
          {role.items.map((item, index) => (
            <article className="role-card" key={item.title}>
              <span className="role-card-label"><span>0{index + 1}</span>{item.tag}</span>
              <h4>{item.title}</h4><p>{item.copy}</p>
            </article>
          ))}
        </div>
      </div>
      <p className="role-access-note">Die verfügbaren Bereiche richten sich nach deiner Rolle, deinen Teams und den erteilten Freigaben.</p>
    </div>
  );
}

function parseFeatureCatalog(source: string): CatalogGroup[] {
  const groups: CatalogGroup[] = [];
  let currentGroup: CatalogGroup | null = null;
  let currentItem: CatalogItem | null = null;

  source.split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trimEnd();
    if (!line.trim()) return;

    const bullet = line.match(/^(\s*)-\s+(.*)$/);
    if (!bullet) {
      currentGroup = { title: line.trim(), items: [] };
      groups.push(currentGroup);
      currentItem = null;
      return;
    }

    if (!currentGroup) return;
    const [, indentation, text] = bullet;
    if (indentation.length === 0) {
      currentItem = { text, details: [] };
      currentGroup.items.push(currentItem);
    } else if (currentItem) {
      currentItem.details.push(text);
    }
  });

  return groups;
}

const featureCatalog = parseFeatureCatalog(featureCatalogSource);
const catalogFunctionCount = featureCatalog.reduce((sum, group) => sum + group.items.length, 0);
const catalogDetailCount = featureCatalog.reduce(
  (sum, group) => sum + group.items.reduce((groupSum, item) => groupSum + 1 + item.details.length, 0),
  0,
);

function filterFeatureCatalog(groups: CatalogGroup[], query: string): CatalogGroup[] {
  const normalizedQuery = query.trim().toLocaleLowerCase("de");
  if (!normalizedQuery) return groups;

  return groups.flatMap((group) => {
    if (group.title.toLocaleLowerCase("de").includes(normalizedQuery)) return [group];

    const items = group.items.flatMap((item) => {
      if (item.text.toLocaleLowerCase("de").includes(normalizedQuery)) return [item];
      const details = item.details.filter((detail) => detail.toLocaleLowerCase("de").includes(normalizedQuery));
      return details.length ? [{ ...item, details }] : [];
    });

    return items.length ? [{ ...group, items }] : [];
  });
}

function useReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>("[data-reveal]");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <img src="/fc-teugn-talents-logo.png" alt="" />
    </span>
  );
}

function ClubCrest() {
  return (
    <span className="club-crest" aria-hidden="true">
      <img src="/fc-teugn-vereinswappen.png" alt="" />
    </span>
  );
}

function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  return (
    <header className="site-header">
      <div className="shell header-inner">
        <a className="brand" href="/" aria-label="FC Teugn Talents – Startseite">
          <ClubCrest />
          <span className="brand-copy">
            <strong>FC TEUGN</strong>
            <span>TALENTS</span>
          </span>
        </a>

        <nav className={`main-nav ${open ? "is-open" : ""}`} aria-label="Hauptnavigation">
          <a href="/#funktionen" onClick={() => setOpen(false)}>Funktionen</a>
          <a href="/#app" onClick={() => setOpen(false)}>Die App</a>
          <a href="/install" onClick={() => setOpen(false)}>Installation</a>
          <a className="nav-cta" href={siteConfig.appUrl}>
            App öffnen <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </nav>

        <button
          className="menu-button"
          type="button"
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="shell footer-inner">
        <a className="brand footer-brand" href="/">
          <BrandMark />
          <span className="brand-copy">
            <strong>FC TEUGN</strong>
            <span>TALENTS</span>
          </span>
        </a>
        <p>Alles rund um dein Team – an einem Ort.</p>
        <nav aria-label="Rechtliches">
          <a href="/datenschutz">Datenschutz</a>
          <a href="/impressum">Impressum</a>
        </nav>
      </div>
    </footer>
  );
}

function AppFrame({ children }: { children: React.ReactNode }) {
  return (
    <>
      <a className="skip-link" href="#main">Zum Inhalt springen</a>
      <Header />
      {children}
      <Footer />
    </>
  );
}

function HeroPhone() {
  return (
    <div className="hero-device-wrap" aria-label="Aktuelle Spieltagübersicht der FC Teugn Talents App">
      <div className="device-glow" />
      <div className="phone phone-hero">
        <div className="phone-top"><span /><span className="speaker" /><span /></div>
        <div className="phone-screen original-media-screen">
          <img src="/app-media/spiele-2026-09.png" width="430" height="932" alt={`Spieltagübersicht der FC Teugn Talents App, aufgenommen am ${screenshotDate}`} />
        </div>
      </div>
      <div className="floating-pill pill-live"><span /> ECHTE APP-ANSICHT</div>
    </div>
  );
}

function FeaturePreviewModal({ feature, onClose }: { feature: Feature; onClose: () => void }) {
  const [imageMissing, setImageMissing] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const Icon = feature.icon;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])'),
      ).filter((element) => !element.hasAttribute("disabled"));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="feature-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="feature-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feature-modal-title"
        aria-describedby="feature-modal-description"
      >
        <button className="feature-modal-close" type="button" onClick={onClose} aria-label="Vorschau schließen" autoFocus>
          <X size={21} aria-hidden="true" />
        </button>
        <div className={`feature-modal-stage motion-${feature.motion}`}>
          {!imageMissing ? (
            <div className="preview-device-scene">
              <span className="preview-device-button preview-device-button-top" aria-hidden="true" />
              <span className="preview-device-button preview-device-button-bottom" aria-hidden="true" />
              <div className="preview-device">
                <span className="preview-device-earpiece" aria-hidden="true" />
                <div className="preview-device-screen">
                  <img
                    src={`/app-media/${feature.previewFile}`}
                    alt={`${feature.title}, aufgenommen am ${screenshotDate}, Namen durch neutrale Bezeichnungen ersetzt`}
                    width="430"
                    height="932"
                    onError={() => setImageMissing(true)}
                  />
                </div>
                <span className="preview-device-reflection" aria-hidden="true" />
              </div>
              <div className="preview-motion-card" aria-hidden="true">
                <span className="preview-motion-icon"><Icon size={20} strokeWidth={1.9} /></span>
                <span className="preview-motion-copy">
                  <small>{feature.motionLabel}</small>
                  <strong>{feature.motionValue}</strong>
                </span>
                <i />
              </div>
            </div>
          ) : (
            <div className="feature-preview-fallback">
              <BrandMark />
              <span className="feature-preview-fallback-icon"><Icon size={43} strokeWidth={1.5} /></span>
              <small>VORSCHAU NICHT VERFÜGBAR</small>
              <strong>{feature.title}</strong>
              <p>Öffne die App, um die aktuelle Ansicht zu sehen.</p>
            </div>
          )}
        </div>
        <div className="feature-modal-copy">
          <p className="feature-modal-index">FUNKTION {feature.number}</p>
          <span className="feature-modal-icon"><Icon size={26} strokeWidth={1.7} /></span>
          <h2 id="feature-modal-title">{feature.title}</h2>
          <p id="feature-modal-description">{feature.copy}</p>
          <div className="feature-modal-status">
            <ImageIcon size={16} aria-hidden="true" />
            {imageMissing ? "Das Beispielbild konnte nicht geladen werden." : `Originalaufnahme · ${screenshotDate}`}
          </div>
          <p className="preview-date-note">Namen und Profilinitialen ersetzt. Statische Vorschau – Inhalte und Freigaben richten sich nach Team und Rolle.</p>
          <a className="button button-primary" href={siteConfig.appUrl}>
            In der App öffnen <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        </div>
      </section>
    </div>
  );
}

function HomePage() {
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [selectedShowcase, setSelectedShowcase] = useState(0);
  const [catalogQuery, setCatalogQuery] = useState("");
  const featureTriggerRef = useRef<HTMLElement | null>(null);
  const visibleCatalog = useMemo(() => filterFeatureCatalog(featureCatalog, catalogQuery), [catalogQuery]);
  const showcaseFeature = showcaseFeatures[selectedShowcase];
  const ShowcaseIcon = showcaseFeature.icon;
  useReveal();

  const openFeaturePreview = (feature: Feature) => {
    featureTriggerRef.current = document.activeElement as HTMLElement;
    setSelectedFeature(feature);
  };

  const closeFeaturePreview = () => {
    setSelectedFeature(null);
    window.setTimeout(() => featureTriggerRef.current?.focus(), 0);
  };

  return (
    <AppFrame>
      <main id="main">
        <section className="hero">
          <div className="hero-backdrop" />
          <div className="hero-grain" />
          <div className="shell hero-grid">
            <div className="hero-copy" data-reveal>
              <p className="eyebrow"><span /> FÜR FAMILIEN. FÜR TRAINER. FÜR TEUGN.</p>
              <h1>DEIN TEAM.<br />DEINE SAISON.<br /><em>EINE APP.</em></h1>
              <p className="hero-lead">
                Termine im Griff. Gemeinsam zum Spiel. Kinder gezielt fördern.
                Mit Familien-Assistent, direkten Mitfahrbuchungen, Lernzielen und Liveticker
                wird aus vielen Absprachen ein gemeinsamer Teamalltag.
              </p>
              <div className="hero-actions">
                <a className="button button-primary" href={siteConfig.appUrl}>
                  App öffnen <ArrowUpRight size={19} aria-hidden="true" />
                </a>
                <a className="button button-ghost" href="/install">
                  App installieren <Download size={18} aria-hidden="true" />
                </a>
              </div>
              <div className="hero-note"><span><Check size={14} /></span> Direkt im Browser · Keine Store-Suche</div>
              <div className="hero-audiences" aria-label="Für wen die App gedacht ist">
                <span>Trainer</span><span>Eltern</span><span>Spieler</span><span>Vereinsverwaltung</span>
              </div>
              <div className="hero-club">
                <ClubCrest />
                <span>Die Team-App für<br /><strong>den FC Teugn</strong></span>
              </div>
            </div>
            <div className="hero-visual" data-reveal>
              <HeroPhone />
            </div>
          </div>
          <a className="scroll-cue" href="#funktionen" aria-label="Zu den Funktionen scrollen">
            <span>ENTDECKEN</span><ArrowRight size={18} />
          </a>
        </section>

        <section className="metric-strip" aria-label="Vorteile der App">
          <div className="shell metrics">
            <div><strong>{catalogFunctionCount}</strong><span>beschriebene Funktionen</span></div>
            <div><strong>{featureCatalog.length}</strong><span>Funktionsbereiche</span></div>
            <div><strong>1</strong><span>zentrale Teamplattform</span></div>
          </div>
        </section>

        <section className="features-section section" id="funktionen">
          <div className="shell">
            <div className="section-heading" data-reveal>
              <p className="eyebrow"><span /> ALLES. EINFACH.</p>
              <h2>WENIGER ORGANISIEREN.<br /><em>MEHR FUSSBALL.</em></h2>
              <p>Neue Möglichkeiten für deinen Vereinsalltag. Wähle deine Perspektive und entdecke, was dir die App abnimmt.</p>
            </div>
            <TeamHighlights />
            <div className="preview-section-heading">
              <h3>Ein Blick in die App</h3>
              <p>Sechs Einblicke direkt aus der App – neu aufgenommen am {screenshotDate}. Antippen und im Smartphone-Rahmen entdecken. Namen sind durch neutrale Bezeichnungen ersetzt.</p>
            </div>
            <div className="feature-grid">
              {features.map((feature) => {
                const { title, copy, icon: Icon, number, wide } = feature;
                return (
                  <button
                    className={`feature-card ${wide ? "feature-wide" : ""}`}
                    type="button"
                    key={title}
                    data-reveal
                    aria-haspopup="dialog"
                    onClick={() => openFeaturePreview(feature)}
                  >
                    <span className="feature-card-media" aria-hidden="true">
                      <img src={`/app-media/${feature.previewFile}`} width="430" height="932" alt="" loading="lazy" />
                    </span>
                    <span className="feature-card-top">
                      <span className="feature-icon"><Icon size={27} strokeWidth={1.7} /></span>
                      <span className="feature-number">{number}</span>
                    </span>
                    <span className="feature-card-copy">
                      <strong>{title}</strong>
                      <span>{copy}</span>
                    </span>
                    <span className="feature-open-label">
                      Beispiel ansehen <ChevronRight size={17} aria-hidden="true" />
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="feature-catalog" data-reveal>
              <div className="feature-catalog-intro">
                <div>
                  <p className="eyebrow"><span /> ZUM NACHLESEN UND ENTDECKEN</p>
                  <h3>{catalogFunctionCount} FUNKTIONEN.<br /><em>{featureCatalog.length} BEREICHE.</em></h3>
                </div>
                <div className="feature-catalog-copy">
                  <p>
                    Von Benutzerkonten und Rollen über Mannschaftsorganisation, Spieltag und Training
                    bis zu Lernzielen, Abwesenheiten und Umfragen: Durchsuche den abgeglichenen
                    Funktionsumfang. Welche Funktionen du nutzen kannst, hängt von deiner Rolle und deinen Freigaben ab.
                  </p>
                  <span>{catalogDetailCount} einzeln aufgeführte Funktions- und Detailpunkte</span>
                </div>
              </div>

              <label className="feature-catalog-search">
                <Search size={20} aria-hidden="true" />
                <input
                  type="search"
                  value={catalogQuery}
                  onChange={(event) => setCatalogQuery(event.target.value)}
                  placeholder="Suchen: Lernziele, Mitfahrten, Push …"
                  aria-label="Funktionsumfang durchsuchen"
                />
                {catalogQuery && (
                  <button type="button" onClick={() => setCatalogQuery("")} aria-label="Suche zurücksetzen">
                    <X size={17} aria-hidden="true" />
                  </button>
                )}
              </label>

              {visibleCatalog.length ? (
                <div className="feature-catalog-grid">
                  {visibleCatalog.map((group) => {
                    const groupDetailCount = group.items.reduce((sum, item) => sum + 1 + item.details.length, 0);
                    return (
                      <details className="feature-catalog-group" key={group.title} open={catalogQuery.trim() ? true : undefined}>
                        <summary>
                          <span className="feature-catalog-index">{String(featureCatalog.findIndex((item) => item.title === group.title) + 1).padStart(2, "0")}</span>
                          <span className="feature-catalog-title">
                            <strong>{group.title}</strong>
                            <small>{groupDetailCount} Funktionen &amp; Details</small>
                          </span>
                          <span className="feature-catalog-toggle"><Plus size={19} aria-hidden="true" /></span>
                        </summary>
                        <div className="feature-catalog-body">
                          <ul>
                            {group.items.map((item) => (
                              <li key={item.text}>
                                <span className="feature-catalog-check"><Check size={13} aria-hidden="true" /></span>
                                <div>
                                  <span>{item.text}</span>
                                  {item.details.length > 0 && (
                                    <ul>
                                      {item.details.map((detail) => <li key={detail}>{detail}</li>)}
                                    </ul>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </details>
                    );
                  })}
                </div>
              ) : (
                <p className="feature-catalog-empty">Keine passende Funktion gefunden. Probiere einen anderen Suchbegriff.</p>
              )}
            </div>
          </div>
        </section>

        <section className="showcase-section section" id="app">
          <div className="showcase-orb" />
          <div className="shell">
            <div className="section-heading heading-split" data-reveal>
              <div>
                <p className="eyebrow"><span /> DIE APP IN AKTION</p>
                <h2>ECHTE APP.<br /><em>ECHTE EINBLICKE.</em></h2>
              </div>
              <p>So sieht Teamorganisation heute aus. Entdecke die aktuelle Oberfläche im Smartphone-Rahmen – direkt aus der App aufgenommen am {screenshotDate}, mit neutralen Namensbezeichnungen.</p>
            </div>
            <div className="showcase-console" data-reveal>
              <div className="showcase-selector" role="tablist" aria-label="App-Ansicht auswählen">
                {showcaseFeatures.map((feature, index) => {
                  const Icon = feature.icon;
                  const active = selectedShowcase === index;
                  return (
                    <button
                      key={feature.title}
                      type="button"
                      role="tab"
                      id={`showcase-tab-${index}`}
                      aria-selected={active}
                      aria-controls="showcase-panel"
                      tabIndex={active ? 0 : -1}
                      onKeyDown={moveTab}
                      className={active ? "is-active" : ""}
                      onClick={() => setSelectedShowcase(index)}
                    >
                      <span className="showcase-selector-icon"><Icon size={20} strokeWidth={1.8} /></span>
                      <span><small>ANSICHT 0{index + 1}</small><strong>{feature.title}</strong></span>
                      <ChevronRight size={18} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>

              <ShowcasePhone key={showcaseFeature.title} feature={showcaseFeature} />

              <div className="showcase-console-copy" id="showcase-panel" role="tabpanel" aria-labelledby={`showcase-tab-${selectedShowcase}`} tabIndex={0}>
                <p>BEISPIEL AUS DER APP</p>
                <span className="showcase-console-icon"><ShowcaseIcon size={24} strokeWidth={1.7} /></span>
                <h3>{showcaseFeature.title}</h3>
                <p>{showcaseFeature.copy}</p>
                <ul>
                  <li><Check size={14} /> Aufgenommen am {screenshotDate}</li>
                  <li><Check size={14} /> Namen und Profilinitialen ersetzt</li>
                  <li><Check size={14} /> Für Smartphone und Browser</li>
                </ul>
                <button className="button button-ghost" type="button" onClick={() => openFeaturePreview(showcaseFeature)}>
                  Groß ansehen <ArrowUpRight size={17} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="install-teaser section" id="installation">
          <div className="shell install-card" data-reveal>
            <div className="install-copy">
              <p className="eyebrow"><span /> INSTALLIEREN. ANMELDEN. LOSLEGEN.</p>
              <h2>DEIN TEAM<br /><em>IN DER TASCHE.</em></h2>
              <p>Android-App herunterladen, auf dem iPhone zum Home-Bildschirm hinzufügen oder direkt im Browser starten. Dein vorhandener Zugang bleibt derselbe.</p>
              <div className="install-actions">
                <a className="button button-primary" href="/install">
                  Installationsanleitung <ArrowRight size={18} />
                </a>
                <a className="text-link" href={siteConfig.appUrl}>Im Browser öffnen <ExternalLink size={16} /></a>
              </div>
            </div>
            <div className="device-options" aria-hidden="true">
              <div><Smartphone /><strong>iPhone</strong><span>Zum Home-Bildschirm</span></div>
              <div className="active"><Download /><strong>Android</strong><span>App installieren</span></div>
              <div><Monitor /><strong>Desktop</strong><span>Im Browser öffnen</span></div>
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta-bg" />
          <div className="shell final-cta-inner" data-reveal>
            <ClubCrest />
            <p>FC TEUGN TALENTS</p>
            <h2>ALLES RUND UM<br />DEIN TEAM.</h2>
            <a className="button button-primary" href={siteConfig.appUrl}>
              App öffnen <ArrowUpRight size={19} />
            </a>
          </div>
        </section>
      </main>
      {selectedFeature && <FeaturePreviewModal feature={selectedFeature} onClose={closeFeaturePreview} />}
    </AppFrame>
  );
}

function ShowcasePhone({ feature }: { feature: Feature }) {
  return (
    <article className="showcase-item">
      <div className="phone showcase-phone">
        <div className="phone-top"><span /><span className="speaker" /><span /></div>
        <div className="phone-screen showcase-media-screen">
          <img
            src={`/app-media/${feature.previewFile}`}
            width="430"
            height="932"
            alt={`${feature.title} in der FC Teugn Talents App, Stand ${screenshotDate}`}
          />
        </div>
      </div>
    </article>
  );
}

function detectDevice(): Device {
  const userAgent = navigator.userAgent;
  const isiPad = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(userAgent) || isiPad) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "desktop";
}

const installContent: Record<Device, { label: string; icon: LucideIcon; title: string; intro: string; steps: { title: string; copy: string; icon: LucideIcon }[] }> = {
  ios: {
    label: "iPhone / iPad",
    icon: Smartphone,
    title: "Auf iPhone installieren",
    intro: "Öffne die Web-App in Safari und lege sie mit wenigen Handgriffen als echte Web-App auf deinem Home-Bildschirm ab.",
    steps: [
      { title: "In Safari öffnen", copy: "Öffne die Web-App über den Button unten im Safari-Browser.", icon: ExternalLink },
      { title: "Teilen auswählen", copy: "Tippe in Safari auf das Teilen-Symbol. Je nach Safari-Ansicht findest du es zuerst unter „Mehr“.", icon: Share2 },
      { title: "Als Web-App hinzufügen", copy: "Wähle „Zu Home-Bildschirm hinzufügen“, aktiviere „Als Web-App öffnen“ und tippe auf „Hinzufügen“.", icon: Plus },
    ],
  },
  android: {
    label: "Android",
    icon: Smartphone,
    title: "Android-App herunterladen",
    intro: "Die aktuelle Android-Version wird über den FC-Teugn-Talents-Download in der MagentaCloud bereitgestellt.",
    steps: [
      { title: "Downloadseite öffnen", copy: "Tippe unten auf „Android-App herunterladen“. Die MagentaCloud öffnet sich in einem neuen Fenster.", icon: ExternalLink },
      { title: "App-Datei herunterladen", copy: "Lade die im Vereinsdownload angebotene aktuelle APK-Datei herunter. Wähle für eine Neuinstallation keine ältere Version aus dem Archiv.", icon: Download },
      { title: "Installation bestätigen", copy: "Öffne die APK. Falls Android nachfragt, erlaube deinem Browser vorübergehend „Apps aus dieser Quelle installieren“. Bestätige die Installation und schalte diese Erlaubnis danach wieder aus.", icon: CheckCircle2 },
    ],
  },
  desktop: {
    label: "Desktop",
    icon: Monitor,
    title: "Am Computer verwenden",
    intro: "Am Desktop funktioniert FC Teugn Talents direkt im Browser – ganz ohne Installation.",
    steps: [
      { title: "Web-App öffnen", copy: "Starte die App über den Button unten in deinem aktuellen Browser.", icon: ExternalLink },
      { title: "Anmelden", copy: "Melde dich mit deinem vorhandenen Zugang bei deinem Team an.", icon: UsersRound },
      { title: "Als Favorit speichern", copy: "Speichere die Seite als Lesezeichen, damit du sie schnell wiederfindest.", icon: CheckCircle2 },
    ],
  },
};

const installPlatformNote: Record<Device, string> = {
  ios: "Safari · Home-Bildschirm",
  android: "APK · direkter Download",
  desktop: "Browser · ohne Installation",
};

function InstallPage() {
  const [device, setDevice] = useState<Device>("desktop");
  const [shared, setShared] = useState(false);
  const [shareMessage, setShareMessage] = useState("");

  useEffect(() => setDevice(detectDevice()), []);
  const content = useMemo(() => installContent[device], [device]);
  const primaryAction = device === "android"
    ? { href: siteConfig.androidDownloadUrl, label: "Android-App herunterladen", icon: Download }
    : device === "ios"
      ? { href: siteConfig.appUrl, label: "Web-App öffnen", icon: ExternalLink }
      : { href: siteConfig.appUrl, label: "App im Browser öffnen", icon: ArrowUpRight };
  const PrimaryActionIcon = primaryAction.icon;

  const shareInstallLink = async () => {
    const shareData = {
      title: "FC Teugn Talents",
      text: "Hier findest du die FC Teugn Talents App.",
      url: `${siteConfig.siteUrl}/install`,
    };
    setShareMessage("");
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareData.url);
      setShared(true);
      setShareMessage("Der Link zur Anleitung wurde kopiert.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setShared(false);
      setShareMessage(`Teilen ist hier nicht verfügbar. Kopiere diese Adresse: ${shareData.url}`);
    }
  };

  return (
    <AppFrame>
      <main id="main" className="install-page">
        <div className="install-page-bg" />
        <section className="shell install-page-inner">
          <a className="back-link" href="/"><ArrowLeft size={17} /> Zurück zur Startseite</a>
          <div className="install-page-hero">
            <div className="install-page-heading">
              <p className="eyebrow"><span /> SCHNELL STARTKLAR</p>
              <h1>DEINE APP.<br /><em>DEIN GERÄT.</em></h1>
              <p>Wähle dein Gerät. Die passende Anleitung erscheint sofort – klar, kurz und ohne technische Umwege.</p>
              <div className="install-hero-proof">
                <span><Check size={13} /> Kostenlos</span>
                <span><Check size={13} /> Direkter Vereinsdownload</span>
                <span><Check size={13} /> In wenigen Schritten</span>
              </div>
            </div>

            <aside className="install-snapshot" aria-label="FC Teugn Talents Installationsübersicht">
              <div className="install-snapshot-top">
                <BrandMark />
                <span><i /> BEREIT ZUR INSTALLATION</span>
              </div>
              <p>FC TEUGN TALENTS</p>
              <h2>Ein Zugang.<br />Auf jedem Gerät.</h2>
              <div className="install-snapshot-platforms">
                <span><Smartphone size={18} /> iPhone</span>
                <span><Download size={18} /> Android</span>
                <span><Monitor size={18} /> Desktop</span>
              </div>
            </aside>
          </div>

          <div className="device-tabs" role="tablist" aria-label="Gerät auswählen">
            {(Object.keys(installContent) as Device[]).map((key) => {
              const Icon = installContent[key].icon;
              return (
                <button
                  type="button"
                  role="tab"
                  id={`device-tab-${key}`}
                  aria-selected={device === key}
                  aria-controls="device-panel"
                  tabIndex={device === key ? 0 : -1}
                  onKeyDown={moveTab}
                  className={device === key ? "active" : ""}
                  key={key}
                  onClick={() => setDevice(key)}
                >
                  <span className="device-tab-icon"><Icon size={21} /></span>
                  <span><strong>{installContent[key].label}</strong><small>{installPlatformNote[key]}</small></span>
                  <CheckCircle2 className="device-tab-check" size={18} />
                </button>
              );
            })}
          </div>

          <div className="instruction-card" id="device-panel" role="tabpanel" aria-labelledby={`device-tab-${device}`} tabIndex={0}>
            <div className="instruction-intro">
              <span className="instruction-icon"><content.icon size={27} /></span>
              <div><h2>{content.title}</h2><p>{content.intro}</p></div>
              <span className="instruction-duration">3 SCHRITTE</span>
            </div>
            {device === "ios" && (
              <div className="ios-install-notice">
                <span className="ios-install-notice-icon"><Smartphone size={22} /></span>
                <div>
                  <strong>Warum gibt es keinen Ein-Klick-Button?</strong>
                  <p>Apple erlaubt Websites nicht, sich automatisch auf dem Home-Bildschirm zu installieren. Die Bestätigung über das Safari-Menü ist daher einmalig erforderlich.</p>
                </div>
                <a href="https://support.apple.com/de-de/guide/iphone/iphea86e5236/ios" target="_blank" rel="noreferrer">
                  Apple-Anleitung <ArrowUpRight size={15} />
                </a>
              </div>
            )}
            <ol className="instruction-steps">
              {content.steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <li key={step.title}>
                    <span className="step-number">{String(index + 1).padStart(2, "0")}</span>
                    <span className="step-icon"><Icon size={21} /></span>
                    <div><h3>{step.title}</h3><p>{step.copy}</p></div>
                  </li>
                );
              })}
            </ol>
            <div className="instruction-actions">
              <a
                className="button button-primary"
                href={primaryAction.href}
                target="_blank"
                rel="noreferrer"
              >
                {primaryAction.label} <PrimaryActionIcon size={19} />
              </a>
              <button className="button button-ghost" type="button" onClick={shareInstallLink}>
                {shared ? <Check size={18} /> : <Share2 size={18} />}
                {shared ? "Link kopiert" : "Anleitung teilen"}
              </button>
            </div>
            <p className="share-feedback" role="status">{shareMessage}</p>
            <div className="after-install">
              <h3>Schon installiert? So bleibst du startklar.</h3>
              <div className="after-install-grid">
                <div><strong>Updates direkt in der App</strong>
                  <p>{device === "android"
                    ? "Öffne den Infobereich oder „Installation & Updates“ und wähle „Nach Updates suchen“. Installiere das Update über die vorhandene App – nicht vorher deinstallieren. Android fragt dich vor der Installation."
                    : "Die Web-App weist auf neue Versionen hin. Bestätige das angebotene Neuladen oder nutze „Nach Updates suchen“ im Infobereich beziehungsweise unter „Installation & Updates“."}</p>
                </div>
                <div><strong>{device === "ios" ? "Push auf dem iPhone" : "Benachrichtigungen aktivieren"}</strong>
                  <p>{device === "ios"
                    ? "Starte die App über ihr Symbol auf dem Home-Bildschirm und aktiviere Push in den App-Einstellungen. Bestätige die iPhone-Abfrage. Falls die App erneut um eine Bestätigung bittet, folge den angezeigten Schritten."
                    : "Aktiviere gewünschte Push-Kategorien in der App und erlaube Benachrichtigungen im Browser beziehungsweise in den Geräteeinstellungen. Ein Stumm- oder Fokusmodus kann Hinweise unterdrücken."}</p>
                </div>
                <div><strong>Dein Zugang bleibt derselbe</strong>
                  <p>Verwende dein bestehendes Konto unter <a href={siteConfig.appUrl}>app.fc-teugn-talents.de</a>. Nach dem Wechsel auf die neue Web-Adresse kann eine erneute Anmeldung nötig sein. Ein neues Konto brauchst du dafür nicht.</p>
                </div>
              </div>
              <p className="release-note">Geprüfter Stand: Web-App und Android {appRelease.version} (Build {appRelease.build}), {appRelease.checkedAt}. Neuere Versionen findest du in der App und im Downloadordner.</p>
            </div>
          </div>
          <div className="install-help">
            <CheckCircle2 size={17} />
            <span>Du brauchst Hilfe? Wende dich an deine Trainerin, deinen Trainer oder die Teamverwaltung.</span>
          </div>
        </section>
      </main>
    </AppFrame>
  );
}

function LegalPage({ type }: { type: "impressum" | "datenschutz" }) {
  const isImprint = type === "impressum";
  const { legal } = siteConfig;

  return (
    <AppFrame>
      <main id="main" className="legal-page">
        <div className="shell legal-inner">
          <a className="back-link" href="/"><ArrowLeft size={17} /> Zurück zur Startseite</a>
          <p className="eyebrow"><span /> RECHTLICHES</p>
          <h1 className="legal-title">{isImprint ? "IMPRESSUM" : "DATENSCHUTZ"}</h1>
          {isImprint ? (
            <div className="legal-content">
              <section className="legal-provider-card">
                <p className="legal-section-label">ANGABEN GEMÄSS § 5 DDG</p>
                <h2>{legal.organization}</h2>
                <div className="legal-facts">
                  <div>
                    <span>Diensteanbieter und Anschrift</span>
                    <p>{legal.organization}<br />{legal.street}<br />{legal.city}<br />Deutschland</p>
                  </div>
                  <div>
                    <span>Vertretungsberechtigt</span>
                    <p>{legal.representedBy}</p>
                  </div>
                  <div>
                    <span>Kontakt</span>
                    <p>E-Mail: <a href={`mailto:${legal.email}`}>{legal.email}</a></p>
                  </div>
                  <div>
                    <span>Vereinsregister</span>
                    <p>{legal.registerCourt}<br />Registernummer: {legal.registerNumber}</p>
                  </div>
                </div>
              </section>
              <section>
                <h2>Redaktionelle Verantwortlichkeit</h2>
                <p>
                  Diese Website ist eine statische Landingpage für die FC-Teugn-Talents-App und enthält derzeit keine
                  journalistisch-redaktionell gestalteten Angebote im Sinne von § 18 Abs. 2 Medienstaatsvertrag. Eine
                  gesonderte verantwortliche Person nach dieser Vorschrift wird deshalb nicht benannt.
                </p>
              </section>
              <section>
                <h2>Hinweise zu eigenen Inhalten</h2>
                <p>
                  Der {legal.organization} ist für die eigenen Inhalte dieser Website nach den allgemeinen Gesetzen
                  verantwortlich. Die Inhalte werden mit angemessener Sorgfalt erstellt und gepflegt. Dennoch können
                  sich insbesondere Termine, Funktionsbeschreibungen, Downloadstände und externe Angebote kurzfristig
                  ändern. Eine Gewähr für die jederzeitige Richtigkeit, Vollständigkeit und Aktualität unverbindlicher
                  Informationen wird daher nicht übernommen. Zwingende gesetzliche Haftungsvorschriften bleiben
                  unberührt.
                </p>
              </section>
              <section>
                <h2>Externe Links</h2>
                <p>
                  Diese Website enthält Links zu extern bereitgestellten Angeboten, insbesondere zur
                  FC-Teugn-Talents-Web-App, zur MagentaCloud und gegebenenfalls zu weiteren Informationsangeboten. Für
                  Inhalte und Datenschutzpraktiken auf externen Seiten ist der jeweilige Anbieter verantwortlich. Zum
                  Zeitpunkt der Verlinkung waren für uns keine offensichtlichen Rechtsverletzungen erkennbar. Eine
                  fortlaufende Kontrolle externer Inhalte ohne konkreten Anlass ist nicht zumutbar. Sobald uns eine
                  konkrete Rechtsverletzung bekannt wird, prüfen wir den Hinweis und entfernen den betroffenen Link,
                  soweit dies rechtlich geboten und technisch möglich ist.
                </p>
              </section>
              <section>
                <h2>Verfügbarkeit von App und Downloads</h2>
                <p>
                  Die Landingpage informiert über die FC-Teugn-Talents-App und verweist auf gesondert bereitgestellte
                  App- und Downloadangebote. Wartungsarbeiten, technische Störungen oder Änderungen bei beteiligten
                  Plattform- und Hostinganbietern können deren Erreichbarkeit vorübergehend einschränken. Aus der
                  bloßen Bereitstellung dieser kostenfreien Landingpage folgt kein Anspruch auf eine jederzeit
                  unterbrechungsfreie Verfügbarkeit. Vertragliche oder zwingende gesetzliche Ansprüche bleiben davon
                  unberührt.
                </p>
              </section>
              <section>
                <h2>Urheber-, Bild- und Kennzeichenrechte</h2>
                <p>
                  Gestaltung, Texte, Grafiken, Logos, Wappen, Fotografien und sonstige Inhalte dieser Website können
                  urheber-, bild- oder kennzeichenrechtlich geschützt sein. Eine Vervielfältigung, Bearbeitung,
                  Verbreitung oder sonstige Nutzung außerhalb der gesetzlichen Schranken bedarf der vorherigen
                  Zustimmung des jeweiligen Rechteinhabers. Inhalte Dritter werden als solche gekennzeichnet, soweit
                  dies erforderlich und erkennbar ist. Vereinswappen und Markenbezeichnungen dürfen nicht in einer
                  Weise verwendet werden, die den Eindruck einer nicht bestehenden offiziellen Verbindung oder
                  Freigabe erweckt.
                </p>
              </section>
              <section>
                <h2>Hinweise auf Rechtsverletzungen</h2>
                <p>
                  Solltest du auf dieser Website einen rechtswidrigen Inhalt, eine fehlerhafte Rechtezuordnung oder
                  einen nicht mehr zulässigen externen Link feststellen, sende bitte einen möglichst konkreten Hinweis
                  an <a href={`mailto:${legal.email}`}>{legal.email}</a>. Wir prüfen nachvollziehbare Hinweise zeitnah
                  und ergreifen bei Bedarf angemessene Maßnahmen.
                </p>
              </section>
            </div>
          ) : (
            <div className="legal-content">
              <section>
                <h2>1. Verantwortlicher</h2>
                <p>
                  Verantwortlich für die Verarbeitung personenbezogener Daten auf dieser Website im Sinne der
                  Datenschutz-Grundverordnung (DSGVO) ist:<br /><br />
                  {legal.organization}<br />
                  Vertreten durch: {legal.representedBy}<br />
                  {legal.street}<br />
                  {legal.city}<br />
                  E-Mail: <a href={`mailto:${legal.email}`}>{legal.email}</a>
                </p>
              </section>
              <section>
                <h2>2. Geltungsbereich und Stand</h2>
                <p>
                  Diese Datenschutzerklärung gilt für die Landingpage unter <strong>fc-teugn-talents.de</strong>,
                  einschließlich ihrer Unterseiten und einer gegebenenfalls eingerichteten Weiterleitung von
                  <strong> www.fc-teugn-talents.de</strong>. Die verlinkte FC-Teugn-Talents-Web-App ist ein
                  eigenständiges Angebot; für sie gelten die dort bereitgestellten Datenschutzhinweise.
                  <br /><br />Stand: 17. September 2026
                </p>
              </section>
              <section>
                <h2>3. Aufruf der Website und Hosting</h2>
                <p>
                  Diese Website wird auf unserem Server bei <strong>Contabo</strong> bereitgestellt.
                  Beim Aufruf übermittelt dein Browser technisch erforderliche Verbindungs- und Zugriffsdaten an die
                  Hosting-Infrastruktur. Dazu können insbesondere IP-Adresse, Datum und Uhrzeit des Abrufs,
                  aufgerufene Adresse, übertragene Datenmenge, Referrer, Browsertyp und -version, Betriebssystem sowie
                  der HTTP-Status gehören.
                </p>
                <p>
                  Die Verarbeitung erfolgt, um die Website zuverlässig und sicher auszuliefern, Angriffe und
                  missbräuchliche Nutzung zu erkennen sowie technische Fehler zu untersuchen. Rechtsgrundlage ist
                  Art. 6 Abs. 1 Buchst. f DSGVO. Unser berechtigtes Interesse liegt in einem sicheren, stabilen und
                  wirtschaftlichen Betrieb dieses Informationsangebots. Ohne die Verarbeitung der IP-Adresse kann die
                  Website technisch nicht an dein Gerät ausgeliefert werden.
                </p>
                <p>
                  Für die Bereitstellung der Server-Infrastruktur nutzen wir Contabo. Die Website lädt keine
                  Inhalte von Firebase Hosting oder Google Cloud Run nach. Weitere Informationen zur
                  Datenverarbeitung beim Infrastruktur-Anbieter findest du in den
                  <a href="https://contabo.com/en/legal/privacy/" target="_blank" rel="noreferrer"> Datenschutzhinweisen von Contabo</a>.
                </p>
                <p>
                  Für diese Landingpage ist keine eigene Besucherprotokollierung aktiviert. Technische
                  Betriebs- und Fehlerprotokolle können zur Sicherung des Betriebs anfallen; sie werden
                  nicht für eine Analyse deines Nutzungsverhaltens verwendet.
                </p>
              </section>
              <section>
                <h2>4. Cookies, Endgerätespeicher und Reichweitenmessung</h2>
                <p>
                  Diese Landingpage setzt keine Cookies ein. Sie verwendet weder Firebase Analytics noch andere
                  Analyse-, Marketing-, Profiling- oder Social-Media-Tracker. Beim bloßen Seitenaufruf werden keine
                  Inhalte von sozialen Netzwerken, Videoportalen, Kartendiensten oder externen Schriftanbietern
                  nachgeladen. Ein Einwilligungsbanner ist daher für den gegenwärtigen technischen Stand nicht
                  erforderlich.
                </p>
                <p>
                  Der Browser kann statische Dateien im technisch erforderlichen Zwischenspeicher ablegen, damit die
                  ausdrücklich aufgerufene Website dargestellt und bei weiteren Aufrufen effizient geladen werden
                  kann. Soweit darin ein Speichern oder Auslesen von Informationen im Endgerät liegt, erfolgt dies auf
                  Grundlage von § 25 Abs. 2 Nr. 2 TDDDG. Die Erkennung der Geräteart für die passende
                  Installationsanleitung erfolgt ausschließlich lokal im Browser, wird nicht dauerhaft gespeichert und
                  nicht an uns übermittelt.
                </p>
              </section>
              <section>
                <h2>5. Externe Links, App-Aufruf und Android-Download</h2>
                <p>
                  Diese Landingpage enthält normale Verknüpfungen zu externen Angeboten. Dazu gehören insbesondere
                  die ebenfalls auf unserem Contabo-Server bereitgestellte FC-Teugn-Talents-Web-App unter
                  <strong> app.fc-teugn-talents.de</strong>, der Android-Download über
                  die <strong>MagentaCLOUD der Telekom Deutschland GmbH</strong>, die Installationshilfe von
                  <strong> Apple Support</strong> sowie die in dieser Datenschutzerklärung genannten Informationsseiten
                  von Google/Firebase, Telekom, Apple und Datenschutzaufsichtsbehörden.
                </p>
                <p>
                  Beim bloßen Laden der Landingpage wird zu diesen Zielseiten keine Verbindung aufgebaut. Erst wenn
                  du einen externen Link aktiv auswählst, ruft dein Browser die Zielseite auf. Der jeweilige Anbieter
                  erhält dann die für den Verbindungsaufbau erforderlichen Daten, insbesondere deine IP-Adresse,
                  Datum und Uhrzeit des Abrufs, Browser- und Geräteinformationen sowie die angeforderte Zieladresse.
                  Für die weitere Verarbeitung auf der Zielseite ist grundsätzlich deren Betreiber verantwortlich;
                  es gelten die dort veröffentlichten Datenschutzhinweise.
                </p>
                <p>
                  Zum Schutz deiner Privatsphäre liefert die Landingpage die technische Vorgabe
                  <strong> „no-referrer“</strong> aus. Unterstützt der verwendete Browser diese Vorgabe, wird beim
                  Wechsel zu einer externen Seite keine Herkunftsadresse dieser Landingpage im Referrer-Header
                  übermittelt. Die technisch notwendige Übertragung der IP-Adresse an den Zielanbieter wird dadurch
                  nicht verhindert.
                </p>
                <p>
                  Wir stellen diese Links bereit, damit die ausdrücklich gewünschte App, Installationsdatei oder
                  Hilfestellung erreichbar ist. Soweit hierbei auf unserer Landingpage eine Verarbeitung
                  personenbezogener Daten stattfindet, beruht sie auf Art. 6 Abs. 1 Buchst. f DSGVO. Unser
                  berechtigtes Interesse liegt in einer sicheren und nutzerfreundlichen Bereitstellung dieser
                  Angebote. Weitere Informationen findest du in der
                  <a href="https://policies.google.com/privacy?hl=de" target="_blank" rel="noreferrer"> Datenschutzerklärung von Google</a>, den
                  <a href="https://www.telekom.de/datenschutzhinweise" target="_blank" rel="noreferrer"> Datenschutzhinweisen der Telekom</a> und der
                  <a href="https://www.apple.com/de/legal/privacy/" target="_blank" rel="noreferrer"> Datenschutzrichtlinie von Apple</a>.
                </p>
              </section>
              <section>
                <h2>6. Teilen-Funktion und Zwischenablage</h2>
                <p>
                  Auf der Installationsseite kannst du den Link zur Anleitung über die Teilen-Funktion deines
                  Browsers oder Betriebssystems weitergeben. Die Funktion wird ausschließlich nach deinem aktiven
                  Klick aufgerufen. Die Auswahl des Empfängers beziehungsweise einer installierten App erfolgt in der
                  systemeigenen Teilen-Ansicht. Wir erhalten weder die Auswahl noch Informationen darüber, ob oder an
                  wen du den Link weitergibst. Für eine anschließende Verarbeitung durch die von dir ausgewählte App
                  oder den ausgewählten Kommunikationsdienst gelten deren Datenschutzhinweise.
                </p>
                <p>
                  Unterstützt dein Gerät keine Teilen-Ansicht, wird nach deinem Klick lediglich die öffentliche
                  Adresse dieser Installationsseite in die lokale Zwischenablage deines Geräts geschrieben. Die
                  Landingpage liest keine Inhalte aus deiner Zwischenablage aus und übermittelt den kopierten Link
                  nicht an uns.
                </p>
              </section>
              <section>
                <h2>7. Kontaktaufnahme per E-Mail</h2>
                <p>
                  Wenn du uns per E-Mail kontaktierst, verarbeiten wir deine Absenderadresse, den Inhalt deiner
                  Nachricht und die von dir freiwillig mitgeteilten Daten zur Bearbeitung des Anliegens. Je nach Inhalt
                  beruht dies auf Art. 6 Abs. 1 Buchst. b DSGVO, wenn es um ein Vertragsverhältnis oder vorvertragliche
                  Maßnahmen geht, oder auf Art. 6 Abs. 1 Buchst. f DSGVO bei allgemeinen Anfragen. Unser berechtigtes
                  Interesse liegt in der sachgerechten Beantwortung der Anfrage. Die Daten werden gelöscht, sobald die
                  Anfrage abschließend erledigt ist und keine gesetzlichen Aufbewahrungspflichten oder sonstigen
                  berechtigten Gründe für eine weitere Speicherung bestehen. E-Mail-Kommunikation kann technisch über
                  die beteiligten Mailanbieter verarbeitet werden.
                </p>
              </section>
              <section>
                <h2>8. Speicherdauer</h2>
                <p>
                  Personenbezogene Daten werden nur so lange verarbeitet, wie es für den jeweiligen Zweck erforderlich
                  ist. Anschließend werden sie gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten,
                  Nachweisinteressen oder die Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen eine
                  weitere Speicherung rechtfertigen. Informationen zur technischen Bereitstellung dieses
                  Informationsangebots findest du in Abschnitt 3.
                </p>
              </section>
              <section>
                <h2>9. Deine Datenschutzrechte</h2>
                <p>Unter den jeweiligen gesetzlichen Voraussetzungen hast du insbesondere folgende Rechte:</p>
                <ul>
                  <li>Auskunft über deine verarbeiteten personenbezogenen Daten (Art. 15 DSGVO),</li>
                  <li>Berichtigung unrichtiger oder Vervollständigung unvollständiger Daten (Art. 16 DSGVO),</li>
                  <li>Löschung deiner personenbezogenen Daten (Art. 17 DSGVO),</li>
                  <li>Einschränkung der Verarbeitung (Art. 18 DSGVO),</li>
                  <li>Datenübertragbarkeit (Art. 20 DSGVO) sowie</li>
                  <li>Widerspruch gegen eine Verarbeitung auf Grundlage von Art. 6 Abs. 1 Buchst. f DSGVO (Art. 21 DSGVO).</li>
                </ul>
                <p>
                  Ein Widerspruch wirkt für die Zukunft. Zur Ausübung deiner Rechte genügt eine Nachricht an
                  <a href={`mailto:${legal.email}`}> {legal.email}</a>. Außerdem hast du gemäß Art. 77 DSGVO das Recht,
                  dich bei einer Datenschutzaufsichtsbehörde zu beschweren.
                </p>
              </section>
              <section>
                <h2>10. Zuständige Datenschutzaufsichtsbehörde</h2>
                <p>
                  Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
                  Promenade 18<br />
                  91522 Ansbach<br />
                  Telefon: +49 (0) 981 180093-0<br />
                  E-Mail: poststelle@lda.bayern.de<br />
                  Website: <a href="https://www.lda.bayern.de" target="_blank" rel="noreferrer">www.lda.bayern.de</a><br />
                  Beschwerdeformular: <a href="https://www.lda.bayern.de/de/beschwerde.html" target="_blank" rel="noreferrer">Online-Datenschutzbeschwerde</a>
                </p>
              </section>
              <section>
                <h2>11. Datensicherheit</h2>
                <p>
                  Die Website wird ausschließlich verschlüsselt über HTTPS ausgeliefert. Zusätzlich setzen wir
                  technische Sicherheitsrichtlinien ein, die unter anderem das Nachladen nicht freigegebener externer
                  Inhalte, das Einbetten der Website in fremde Seiten sowie unberechtigte Zugriffe auf Kamera,
                  Mikrofon und Standort über diese Landingpage beschränken. Ein vollständiger Schutz bei der
                  Datenübertragung im Internet kann dennoch nicht garantiert werden.
                </p>
              </section>
              <section>
                <h2>12. Automatisierte Entscheidungen und Pflichtangaben</h2>
                <p>
                  Auf dieser Landingpage findet keine automatisierte Entscheidungsfindung einschließlich Profiling im
                  Sinne von Art. 22 DSGVO statt. Eine gesetzliche oder vertragliche Pflicht zur Bereitstellung
                  personenbezogener Daten besteht für den Besuch nicht. Die Übermittlung der technischen
                  Verbindungsdaten ist jedoch erforderlich, damit die Website ausgeliefert werden kann.
                </p>
              </section>
            </div>
          )}
        </div>
      </main>
    </AppFrame>
  );
}

function NotFoundPage() {
  return (
    <AppFrame>
      <main id="main" className="not-found shell">
        <BrandMark />
        <p className="eyebrow"><span /> 404</p>
        <h1>DIESE SEITE IST<br /><em>NICHT IM KADER.</em></h1>
        <a className="button button-primary" href="/">Zur Startseite <ArrowRight size={18} /></a>
      </main>
    </AppFrame>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return <HomePage />;
  if (path === "/install") return <InstallPage />;
  if (path === "/impressum") return <LegalPage type="impressum" />;
  if (path === "/datenschutz") return <LegalPage type="datenschutz" />;
  return <NotFoundPage />;
}
